/**
 * @file: DefectiveLevel.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

export type DefectiveLevelToken = 'urgent' | 'warning' | 'defective' | 'normal';

export enum DefectiveLevel {
  Urgent = 4,
  Warning = 3,
  Defective = 2,
  Fine = 1,
}

export type DefectiveLevelTokenValueMap<T> = {
  [p in DefectiveLevelToken]: T;
};

export type DefectiveLevelValueMap<T> = {
  [p in DefectiveLevel]: T;
};

export type DefectiveTokenColorMap = DefectiveLevelTokenValueMap<string>;
export type DefectiveLevelColorMap = DefectiveLevelValueMap<string>;

export const defectiveLevelValueMapToTokenMap = <T>(
  valueMap: DefectiveLevelValueMap<T>,
): DefectiveLevelTokenValueMap<T> => {
  return {
    normal: valueMap[DefectiveLevel.Fine],
    defective: valueMap[DefectiveLevel.Defective],
    warning: valueMap[DefectiveLevel.Warning],
    urgent: valueMap[DefectiveLevel.Urgent],
  };
};

export const defectiveLevelTokenMapToValueMap = <T>(
  valueMap: DefectiveLevelTokenValueMap<T>,
): DefectiveLevelValueMap<T> => {
  return {
    [DefectiveLevel.Urgent]: valueMap.urgent,
    [DefectiveLevel.Warning]: valueMap.warning,
    [DefectiveLevel.Defective]: valueMap.defective,
    [DefectiveLevel.Fine]: valueMap.normal,
  };
};

export const DefectiveLevelTokenMap: {
  [p in DefectiveLevel]: DefectiveLevelToken;
} = {
  [DefectiveLevel.Urgent]: 'urgent',
  [DefectiveLevel.Warning]: 'warning',
  [DefectiveLevel.Defective]: 'defective',
  [DefectiveLevel.Fine]: 'normal',
};

export const DefectiveLevelTextShort: {
  [p in DefectiveLevel]: string;
} = {
  [DefectiveLevel.Urgent]: '紧急',
  [DefectiveLevel.Warning]: '异常',
  [DefectiveLevel.Defective]: '轻微',
  [DefectiveLevel.Fine]: '正常',
};

export const DefectiveLevelText: {
  [p in DefectiveLevel]: string;
} = {
  [DefectiveLevel.Urgent]: '急需处理',
  [DefectiveLevel.Warning]: '建议处理',
  [DefectiveLevel.Defective]: '轻微异常',
  [DefectiveLevel.Fine]: '正常',
};

export const DefectiveLevelList: DefectiveLevel[] = [
  DefectiveLevel.Urgent,
  DefectiveLevel.Warning,
  DefectiveLevel.Defective,
  DefectiveLevel.Fine,
];

export type DefectiveSummary = {
  [p in DefectiveLevel]: number;
};

export const makeDefectiveSummary = (): DefectiveSummary => {
  return {
    [DefectiveLevel.Urgent]: 0,
    [DefectiveLevel.Warning]: 0,
    [DefectiveLevel.Defective]: 0,
    [DefectiveLevel.Fine]: 0,
  };
};

export type DefectiveStats = DefectiveSummary;

export const getSumOfDefectiveSummary = (summary: DefectiveSummary) => {
  return (
    summary[DefectiveLevel.Urgent] +
    summary[DefectiveLevel.Warning] +
    summary[DefectiveLevel.Defective] +
    summary[DefectiveLevel.Fine]
  );
};
